import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  feedbacksSelector,
  isPaperCheckInProgressSelector,
  sentenceOffsetsSelector,
} from '@chegg-wt/core-wa/state/selectors'
import getFeatureEditor from '@chegg-wt/core-wa/utils/getEditor'
import { GrammarFeedback, ChangeType, FeedbackType } from '@chegg-wt/core-wa/types'
import { ThreeDots } from '@chegg-wt/core-wa/components/ThreeDots'
import {
  emitComponentViewEvent,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { componentOrigins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamComponentViews'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { ProofReaderCardActions, ToastType } from '@chegg-wt/core-wa/state/types'
import { GRAMMAR_CARD_TEST_ID } from '@chegg-wt/core-wa/constants/paraphraser'
import history from '@chegg-wt/core-wa/utils/history/history'
import { HistoryDeltaAction } from '@chegg-wt/core-wa/utils/history/types'
import { notifyToast } from '@chegg-wt/core-wa/state/actions'
import {
  NoThanksCTA,
  SuggestionCardText,
  SuggestionCardTitle,
  SuggestionCardWrapper,
} from '@chegg-wt/core-wa/components/SuggestionCard/SuggestionCard'
import { adjustFeedbacksInRedux } from '../../utils'
import {
  ButtonsContainer,
  GrammarSuggestionCardText,
  OriginalText,
  ReplacementText,
  UpdateButton,
} from './styled'
import { AddSuggestionText } from './AddTextDescription'

type GrammarCardProps = {
  feedback: GrammarFeedback
  handleCardToggle: () => void
  handleDeleteClick: (isFlagged?: boolean) => void
  isExpanded: boolean
  setFocus: () => void
}

const getGrammerCardTextFromFeedback = ({
  changeType,
  impactedText,
  suggestedText,
}: GrammarFeedback) => {
  switch (changeType) {
    case ChangeType.ADD:
      return (
        <AddSuggestionText impactedText={impactedText} suggestedText={suggestedText} />
      )
    case ChangeType.REPLACE: {
      return (
        <GrammarSuggestionCardText>
          <span>Replace </span>
          <OriginalText>
            <q>{impactedText}</q>
          </OriginalText>
          <span> with </span>
          <ReplacementText>
            <q>{suggestedText}</q>
          </ReplacementText>
        </GrammarSuggestionCardText>
      )
    }
    case ChangeType.REMOVE: {
      return (
        <GrammarSuggestionCardText>
          <span>Remove </span>
          <OriginalText>
            <q>{impactedText}</q>
          </OriginalText>
        </GrammarSuggestionCardText>
      )
    }
    default:
      return null
  }
}

const GrammarCard: React.FC<GrammarCardProps> = ({
  feedback,
  isExpanded,
  handleCardToggle,
  handleDeleteClick,
  setFocus,
}) => {
  const sentenceOffsets = useSelector(sentenceOffsetsSelector)
  const feedbacks = useSelector(feedbacksSelector)
  const isPaperCheckInProgress = useSelector(isPaperCheckInProgressSelector)
  const grammarCardText = getGrammerCardTextFromFeedback(feedback)
  const cardRef = useRef(null)
  const dispatch = useDispatch()

  const suggestionApplyHandler = () => {
    // Add apply card to history
    history.addFeedbackChange({
      feedback,
      action: HistoryDeltaAction.APPLY,
    })

    const editor = getFeatureEditor()
    const { impactedText, suggestedText, id } = feedback
    dispatch(
      userClick({
        origin: origins.proofReaderFeedbackCardClick,
        text: impactedText,
        value: suggestedText,
        id,
        citationMetadata: {
          proofReaderCardtype: FeedbackType.GRAMMAR,
          proofReaderCardAction: ProofReaderCardActions.ACCEPT,
        },
      })
    )

    const impactedSentence = sentenceOffsets.find(
      (sentenceOffset) => sentenceOffset.id === feedback.sentenceId
    )
    const from = impactedSentence.offset + feedback.position.offset
    if (feedback.changeType === ChangeType.REMOVE) {
      editor.commands.removeCustomUnderlineText({
        id: feedback.id,
        offset: from,
        length: feedback.position.length,
        nonManualUpdateType: FeedbackType.GRAMMAR,
      })
    } else {
      // for add and replace change type
      editor.commands.replaceCustomUnderlineText(
        {
          id: feedback.id,
          offset: from,
          length: feedback.position.length,
          nonManualUpdateType: FeedbackType.GRAMMAR,
        },
        feedback.suggestedText
      )
    }
    // adjust feedbacks in redux
    adjustFeedbacksInRedux(feedbacks, feedback, dispatch)

    setFocus()
  }

  useEffect(() => {
    if (isExpanded && cardRef.current) {
      if (!cardRef.current.parentElement.scrollTo) return
      cardRef.current.parentElement.scrollTo({
        top: cardRef.current.offsetTop,
        behavior: 'smooth',
      })
    }
  }, [isExpanded])

  useEffect(() => {
    const { impactedText, suggestedText, id } = feedback
    if (feedback) {
      dispatch(
        emitComponentViewEvent({
          origin: componentOrigins.proofReaderFeedbackCard,
          text: impactedText,
          value: suggestedText,
          id,
          metadata: { proofReaderCardtype: FeedbackType.GRAMMAR },
        })
      )
    }
  }, [])

  const handleThreeDotsClick = () => {
    dispatch(notifyToast(ToastType.FLAG))
    const { impactedText, suggestedText, id } = feedback
    dispatch(
      userClick({
        origin: origins.proofReaderFeedbackCardClick,
        text: impactedText,
        value: suggestedText,
        id,
        citationMetadata: {
          proofReaderCardtype: FeedbackType.GRAMMAR,
          proofReaderCardAction: ProofReaderCardActions.CLICK,
        },
      })
    )
    handleDeleteClick(true)
  }

  return (
    <SuggestionCardWrapper
      ref={cardRef}
      onClick={handleCardToggle}
      isExpanded={isExpanded}
    >
      <SuggestionCardTitle isExpanded={isExpanded}>
        {!isExpanded && (
          <SuggestionCardText isTruncated>{grammarCardText}</SuggestionCardText>
        )}
      </SuggestionCardTitle>

      {isExpanded && (
        <>
          <SuggestionCardText isExpanded={isExpanded}>
            {grammarCardText}
          </SuggestionCardText>
          <ButtonsContainer>
            <UpdateButton
              disabled={isPaperCheckInProgress}
              onClick={suggestionApplyHandler}
              data-test={`${GRAMMAR_CARD_TEST_ID}-update-button`}
            >
              Update
            </UpdateButton>
            <NoThanksCTA
              data-test={`${GRAMMAR_CARD_TEST_ID}-no-thanks-button`}
              disabled={isPaperCheckInProgress}
              onClick={() => handleDeleteClick(false)}
            >
              No thanks
            </NoThanksCTA>
            <ThreeDots
              disabled={isPaperCheckInProgress}
              onFlagClick={handleThreeDotsClick}
              position="topRight"
              data-test={`${GRAMMAR_CARD_TEST_ID}-three-dots-button`}
            />
          </ButtonsContainer>
        </>
      )}
    </SuggestionCardWrapper>
  )
}

export default GrammarCard
